import React from "react";
import { Checklist } from "@culturehq/components";

import { t, Trans } from "app/utils";

type PasswordValidatorOpts = {
  password?: string;
  confirmation?: string;
};

export class PasswordValidator {
  private password: string;

  private confirmation: string;

  constructor(options: PasswordValidatorOpts = {}) {
    this.password = options.password || "";
    this.confirmation = options.confirmation || "";
  }

  mutate(options: PasswordValidatorOpts = {}) {
    return new PasswordValidator(this.optionsFrom(options));
  }

  get lower() {
    return /[a-z]/.test(this.password);
  }

  get upper() {
    return /[A-Z]/.test(this.password);
  }

  get number() {
    return /\d/.test(this.password);
  }

  get length() {
    return this.password.length >= 8;
  }

  get match() {
    return this.password.length > 0 && this.password === this.confirmation;
  }

  matches() {
    return this.password.length > 0 && this.password === this.confirmation;
  }

  getError() {
    if (!this.lower) {
      return t("You must include a lowercase letter");
    }

    if (!this.upper) {
      return t("You must include an uppercase letter");
    }

    if (!this.number) {
      return t("You must include a number");
    }

    if (!this.length) {
      return t("You must enter at least 8 characters long");
    }

    return t("Good so far, you must match the confirm password field");
  }

  isValid() {
    return this.lower && this.upper && this.number && this.length && this.match;
  }

  optionsFrom(options: PasswordValidatorOpts) {
    const { hasOwnProperty } = Object.prototype;

    return {
      password: hasOwnProperty.call(options, "password") ? options.password : this.password,
      confirmation: hasOwnProperty.call(options, "confirmation") ? options.confirmation : this.confirmation
    };
  }
}

type PasswordChecklistProps = {
  validator: PasswordValidator;
};

const PasswordChecklist: React.FC<PasswordChecklistProps> = ({ validator }) => (
  <Checklist>
    <Checklist.Item checked={validator.lower} label={t("Includes a lowercase letter")}>
      <Trans>Includes a lowercase letter</Trans>
    </Checklist.Item>
    <Checklist.Item checked={validator.upper} label={t("Includes an uppercase letter")}>
      <Trans>Includes an uppercase letter</Trans>
    </Checklist.Item>
    <Checklist.Item checked={validator.number} label={t("Includes a number")}>
      <Trans>Includes a number</Trans>
    </Checklist.Item>
    <Checklist.Item checked={validator.length} label={t("Is at least 8 characters long")}>
      <Trans>Is at least 8 characters long</Trans>
    </Checklist.Item>
    <Checklist.Item checked={validator.match} label={t("Matches the confirmation")}>
      <Trans>Matches the confirmation</Trans>
    </Checklist.Item>
  </Checklist>
);

export default PasswordChecklist;
